import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";

const links = [
  { label: "Features", href: "#features", id: "nav-link-features" },
  { label: "Playground", href: "#playground", id: "nav-link-playground" },
  { label: "Students", href: "#students", id: "nav-link-students" },
  { label: "Community", href: "#community", id: "nav-link-community" },
];

const GoogleIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
  </svg>
);

const Navbar = ({ user, onLogout }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const userBlock = user ? (
    <div data-testid="nav-user-menu" className="flex items-center gap-3">
      {user.picture ? (
        <img
          src={user.picture}
          alt={user.name}
          referrerPolicy="no-referrer"
          className="h-8 w-8 rounded-full border border-slate-700"
        />
      ) : (
        <span className="h-8 w-8 rounded-full bg-cyan-500/20 border border-cyan-500/40 flex items-center justify-center text-xs font-semibold text-cyan-300">
          {user.name?.charAt(0)?.toUpperCase()}
        </span>
      )}
      <span className="text-sm text-slate-300 max-w-[140px] truncate">{user.name}</span>
      <button
        data-testid="nav-logout-button"
        onClick={onLogout}
        className="text-[10px] font-mono-x uppercase tracking-widest text-slate-500 hover:text-red-300 transition-colors duration-200"
      >
        Log out
      </button>
    </div>
  ) : null;

  const signInButton = (
    <button
      data-testid="nav-signin-button"
      onClick={login}
      className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-5 py-2 text-sm font-semibold text-slate-300 transition-colors duration-200 hover:border-cyan-500/50 hover:text-cyan-300"
    >
      <GoogleIcon />
      Sign in
    </button>
  );

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color] duration-300 ${
        scrolled || open
          ? "bg-[#090D16]/85 backdrop-blur-xl border-b border-[#1E293B]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <a href="#top" data-testid="nav-logo" className="flex items-center gap-2.5">
          <img src="/logo-icon.png" alt="InNewZ logo" className="h-9 w-9 object-contain" />
          <span className="font-display font-bold text-xl tracking-tight">
            InNew<span className="text-cyan-400">Z</span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.id}
              className="text-sm text-slate-400 hover:text-cyan-400 transition-colors duration-200"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            userBlock
          ) : (
            <>
              {signInButton}
              <a
                href="#waitlist"
                data-testid="nav-cta-button"
                className="inline-flex items-center rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 px-5 py-2 text-sm font-semibold text-white transition-[filter,transform] duration-200 hover:brightness-110 active:scale-95"
              >
                Get Early Access
              </a>
            </>
          )}
        </div>

        <button
          data-testid="nav-mobile-menu-button"
          onClick={() => setOpen((o) => !o)}
          className="md:hidden text-slate-300 p-2"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="md:hidden bg-[#0B111C]/95 backdrop-blur-xl border-b border-[#1E293B] px-6 py-4 space-y-4">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`${l.id}-mobile`}
              onClick={() => setOpen(false)}
              className="block text-sm text-slate-300 hover:text-cyan-400"
            >
              {l.label}
            </a>
          ))}
          {user ? (
            userBlock
          ) : (
            <>
              {signInButton}
              <a
                href="#waitlist"
                data-testid="nav-cta-button-mobile"
                onClick={() => setOpen(false)}
                className="inline-flex items-center rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 px-5 py-2 text-sm font-semibold text-white"
              >
                Get Early Access
              </a>
            </>
          )}
        </div>
      )}
    </header>
  );
};

export default Navbar;
